"use client";

import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { timeAgo } from "@/utils/generic";
import { ChevronLeftIcon, SearchX, Table2 } from "lucide-react";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { Textarea } from "@/components/shadcn/textarea";
import { Toggle } from "@/components/Form/ToggleField";
import type { TableWithCount } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { DataTable } from "../DataTable";
import { SearchBar } from "../SearchBar";
import { TruncatedTextWithTooltip } from "../TruncatedTextWithTooltip";
import { NoxusEmptyBlock } from "../UI/NoxusEmptyBlock";
import BaseDialog from "../Modals/BaseDialog";
import type { AgentTablesEntry } from "./TablesField";

const columnHelper = createColumnHelper<TableWithCount>();

const columns: ColumnDef<TableWithCount, any>[] = [
  columnHelper.accessor("name", {
    cell: (info) => (
      <div className="flex gap-2 items-center">
        <Table2 className="size-4 text-muted-foreground" />
        <TruncatedTextWithTooltip>{info.getValue()}</TruncatedTextWithTooltip>
      </div>
    ),
    header: "Name",
    meta: { width: "45%" },
  }),
  {
    id: "columns",
    header: "Columns",
    accessorFn: (t: TableWithCount) => t.columns.length,
    cell: ({ row }: { row: { original: TableWithCount } }) => (
      <span className="text-sm tabular-nums text-muted-foreground">
        {row.original.columns.length}
      </span>
    ),
    meta: { width: "15%" },
  },
  columnHelper.accessor("row_count", {
    cell: (info) => (
      <span className="text-sm tabular-nums text-muted-foreground">
        {(info.getValue() ?? 0).toLocaleString()}
      </span>
    ),
    header: "Rows",
    meta: { width: "15%" },
  }),
  columnHelper.accessor("updated_at", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue() ? timeAgo(info.getValue()) : "-"}
      </p>
    ),
    header: "Last modified",
    meta: { width: "25%" },
  }),
];

interface AssistantTablePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (entry: AgentTablesEntry) => void;
  filteredTableIds?: string[];
  editingEntry?: AgentTablesEntry;
  editingTableName?: string;
}

export function AssistantTablePicker({
  isOpen,
  onClose,
  onAdd,
  filteredTableIds,
  editingEntry,
  editingTableName,
}: AssistantTablePickerProps) {
  const { spotGroup } = useSpotUser();
  const [search, setSearch] = useState("");
  const [selectedTableId, setSelectedTableId] = useState<string | null>(
    editingEntry?.table_id ?? null
  );
  const [selectedTableName, setSelectedTableName] = useState<string | null>(
    editingTableName ?? null
  );
  const savedContext = editingEntry?.context ?? null;
  const savedContextText =
    typeof savedContext === "string"
      ? savedContext
      : (savedContext?.text ?? "");
  const [context, setContext] = useState<string>(savedContextText);
  const [readOnly, setReadOnly] = useState(editingEntry?.read_only ?? true);
  const [maxRows, setMaxRows] = useState<string>(
    editingEntry?.max_rows != null ? String(editingEntry.max_rows) : ""
  );
  const [queryTimeout, setQueryTimeout] = useState<string>(
    editingEntry?.query_timeout_seconds != null
      ? String(editingEntry.query_timeout_seconds)
      : ""
  );

  const { data, isLoading } = useRemoteData(DATA_KEYS.TABLES, {
    path: { group_id: spotGroup.id },
  });

  const tables = useMemo(() => {
    const excluded = new Set(filteredTableIds ?? []);
    const q = search.trim().toLowerCase();
    return ((data as TableWithCount[] | undefined) ?? [])
      .filter((t) => (t.source ?? "custom") === "custom")
      .filter((t) => !excluded.has(t.id))
      .filter((t) => !q || t.name.toLowerCase().includes(q));
  }, [data, filteredTableIds, search]);

  const handleBack = () => {
    if (!editingEntry) setSelectedTableId(null);
  };

  const handleConfirm = () => {
    if (!selectedTableId) return;
    onAdd({
      table_id: selectedTableId,
      context: context === savedContextText ? savedContext : context || null,
      enabled: editingEntry?.enabled ?? true,
      read_only: readOnly,
      max_rows: maxRows ? Number(maxRows) : null,
      query_timeout_seconds: queryTimeout ? Number(queryTimeout) : null,
    });
    onClose();
  };

  const footerActions = selectedTableId
    ? [
        ...(!editingEntry
          ? [
              {
                label: (
                  <>
                    <ChevronLeftIcon size={12} />
                    Back
                  </>
                ),
                props: {
                  variant: "ghost" as const,
                  onClick: handleBack,
                },
              },
            ]
          : []),
        {
          label: editingEntry ? "Save" : "Add table",
          props: { onClick: handleConfirm },
        },
      ]
    : [
        {
          label: "Cancel",
          props: { variant: "ghost" as const, onClick: onClose },
        },
      ];

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      size="5xl"
      title={
        selectedTableName ? (
          <div className="flex items-center gap-2">
            <Table2 className="size-5 text-muted-foreground shrink-0" />
            {selectedTableName}
          </div>
        ) : (
          "Add table"
        )
      }
      footerActions={footerActions}
    >
      <div className="-m-1 overflow-x-hidden p-1">
        <AnimatePresence mode="wait" initial={false}>
          {!selectedTableId ? (
            <motion.div
              key="step1"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.25 }}
              style={{ width: "100%" }}
            >
              <div className="mb-2">
                <SearchBar
                  placeholder="Search tables"
                  setValue={setSearch}
                  value={search}
                  isDebounced
                  autoFocus
                />
              </div>
              <DataTable
                isLoading={isLoading}
                loadingRows={8}
                columns={columns}
                data={tables}
                rowOnClick={(_id, value) => {
                  setSelectedTableId(value.id);
                  setSelectedTableName(value.name);
                }}
                noDataMessage="No more tables available"
                emptyState={
                  search ? (
                    <NoxusEmptyBlock
                      title="No tables"
                      description={<>No tables match &ldquo;{search}&rdquo;.</>}
                      icon={SearchX}
                      size="sm"
                      button="Clear search"
                      buttonVariant="outline"
                      onClick={() => setSearch("")}
                    />
                  ) : undefined
                }
                noShadow
              />
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.25 }}
              style={{ width: "100%" }}
            >
              <div className="w-full flex flex-col gap-6 overflow-y-auto max-h-[55vh] px-0.5 py-0.5">
                {/* Table context */}
                <div className="flex flex-col gap-1.5">
                  <Label>Table context</Label>
                  <Textarea
                    placeholder="What does this table hold? When should the agent query it?"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                </div>
                {/* Allow edits */}
                <Toggle
                  label="Allow edits"
                  description="Let the agent insert, update, and delete rows. Off keeps the table read-only."
                  value={!readOnly}
                  onChange={(checked) => setReadOnly(!checked)}
                />

                {/* Max rows + Query timeout */}
                <div className="flex gap-4">
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Label>Max rows</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10000}
                      placeholder="100"
                      value={maxRows}
                      onChange={(e) => setMaxRows(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum number of rows the agent can fetch per query.
                    </p>
                  </div>
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Label>Query timeout (s)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={300}
                      placeholder="30"
                      value={queryTimeout}
                      onChange={(e) => setQueryTimeout(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum seconds before a query is cancelled.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BaseDialog>
  );
}
